import React, { useEffect, useMemo, useState } from 'react'
import {
  Modal,
  FormControl,
  ListGroup,
  ListGroupItem,
  Pagination
} from 'react-bootstrap'
import { useDebounce } from 'api/hooks/use-debounce'
import { useAccessManagementGlobalSearch } from 'api/hooks/use-access-management-global-search'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import RoleResult from 'layout/access-management/search/role-result'
import UserResult from 'layout/access-management/search/user-result'
import PermissionResult from 'layout/access-management/search/permission-result'
import ConflictResult from 'layout/access-management/search/conflict-result'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faUserSlash,
  faAddressCard,
  faToggleOn
} from '@fortawesome/free-solid-svg-icons'
import { trackEvent } from '../../google-analytics'

const PER_PAGE = 10

const GlobalSearchModal = ({ show, handleClose }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [dataType, setDataType] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(searchQuery, 500)

  useEffect(() => {
    if (!show) {
      setSearchQuery('')
      setDataType('')
      setPage(1)
    }
  }, [show])

  useEffect(() => {
    trackEvent({
      category: 'Global Search',
      action: 'user_engagement',
      value: debouncedSearch
    })
    setPage(1)
  }, [debouncedSearch])

  useEffect(() => {
    setPage(1)
  }, [dataType])

  const params = useMemo(
    () => ({
      search: debouncedSearch,
      data_type: dataType,
      page,
      per_page: PER_PAGE
    }),
    [debouncedSearch, dataType, page]
  )

  const hasSearch = debouncedSearch.length > 0

  const { data, isLoading, isSuccess } = useAccessManagementGlobalSearch({
    params,
    options: { enabled: hasSearch }
  })

  const results = data?.data ?? []
  const meta = data?.meta ?? {}
  const totalPages = meta.pages ?? 0

  const quickFilterButtons = [
    { text: 'Role', icon: faAddressCard, data_type: 'Role' },
    { text: 'User', icon: faUsers, data_type: 'Employee' },
    { text: 'Permission', icon: faToggleOn, data_type: 'Permission' },
    { text: 'Conflict', icon: faUserSlash, data_type: 'Analytic' }
  ]

  const handleQuickFilter = (clicked) => {
    trackEvent({
      category: 'Global Search',
      action: 'user_engagement',
      value: clicked
    })
    setDataType((current) => (current === clicked ? '' : clicked))
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage)
    }
  }

  const renderPaginationItems = () => {
    const items = []
    const delta = 2
    const left = Math.max(2, page - delta)
    const right = Math.min(totalPages - 1, page + delta)

    items.push(
      <Pagination.Item
        key={1}
        active={page === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    )

    if (left > 2)
      items.push(<Pagination.Ellipsis key="left-ellipsis" disabled />)

    for (let i = left; i <= right; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={page === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      )
    }

    if (right < totalPages - 1)
      items.push(<Pagination.Ellipsis key="right-ellipsis" disabled />)

    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={page === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      )
    }

    return items
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <>
          <FormControl
            type="text"
            placeholder="Search for Roles, Users, Permissions or Conflicts"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border-0 m-0"
            size="lg"
            autoFocus
          />
        </>
      </Modal.Header>
      <Modal.Body>
        <div className="mt-2 mb-4">
          {quickFilterButtons.map((button) => (
            <Button
              key={button.data_type}
              variant={dataType === button.data_type ? 'primary' : 'light'}
              className="me-2"
              onClick={() => handleQuickFilter(button.data_type)}
            >
              <FontAwesomeIcon icon={button.icon} className="me-2" />
              {button.text}
            </Button>
          ))}
        </div>
        <ListGroup variant="flush">
          {isLoading && (
            <ListGroupItem>
              <Spinner animation="grow" variant="warning" />
            </ListGroupItem>
          )}
          {!hasSearch && (
            <ListGroupItem className="text-center mt-4">
              Start typing to search
            </ListGroupItem>
          )}
          {hasSearch && isSuccess && results.length === 0 && (
            <ListGroupItem className="text-center mt-4">
              No results found
              <Button variant="link" onClick={() => setSearchQuery('')}>
                Clear search
              </Button>
            </ListGroupItem>
          )}
          {hasSearch &&
            isSuccess &&
            results.map((result, index) => (
              <React.Fragment
                key={`${result.data_type}-${result.data_id}-${index}`}
              >
                {result.data_type === 'Role' && <RoleResult result={result} />}
                {result.data_type === 'Employee' && (
                  <UserResult result={result} />
                )}
                {result.data_type === 'Permission' && (
                  <PermissionResult result={result} />
                )}
                {result.data_type === 'Analytic' && (
                  <ConflictResult result={result} />
                )}
              </React.Fragment>
            ))}
        </ListGroup>
        {hasSearch && isSuccess && totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination size="sm" className="mb-0">
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
              />
              {renderPaginationItems()}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
              />
            </Pagination>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default GlobalSearchModal
